/**
 * Normalise an uploaded image file to a downscaled JPEG, so uploads behave like
 * camera captures (which are already canvas → JPEG). This fixes the common
 * "upload didn't go through" cases: files over the server's size cap, and
 * formats/rotations that the ID service can't read (a raw phone photo can be
 * 10–20 MB with EXIF orientation; the canvas re-encode strips that).
 *
 * Best-effort: anything we can't decode (e.g. HEIC on a non-Safari browser)
 * falls through to the original File so the server can still try.
 */
export default function normalizeImage(file, maxDim = 1800, quality = 0.9) {
  return new Promise((resolve) => {
    if (!file || typeof file.type !== "string" || !file.type.startsWith("image/")) {
      resolve(file);
      return;
    }
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      try {
        const longest = Math.max(img.naturalWidth, img.naturalHeight) || 1;
        const scale = Math.min(1, maxDim / longest);
        const w = Math.max(1, Math.round(img.naturalWidth * scale));
        const h = Math.max(1, Math.round(img.naturalHeight * scale));
        const canvas = document.createElement("canvas");
        canvas.width = w;
        canvas.height = h;
        canvas.getContext("2d").drawImage(img, 0, 0, w, h);
        canvas.toBlob((blob) => resolve(blob || file), "image/jpeg", quality);
      } catch {
        resolve(file);
      }
    };
    img.onerror = () => { URL.revokeObjectURL(url); resolve(file); };
    img.src = url;
  });
}
